import type {
  DocumentoPadrao,
  DocumentoPadraoEscopo,
  DocumentoPadraoParte,
  DocumentoPadraoProduto,
  DocumentoPadraoSituacaoValidade,
  DocumentoPadraoTipo,
  DocumentosPadraoFiltros,
  DocumentosPadraoSetor,
} from '@/features/documentosPadrao/types/documentosPadraoTypes';

/**
 * FALLBACK, não fonte: o valor que vale é o `diasAvisoVencimento` que a resposta da lista traz.
 * Ele só é usado enquanto a primeira resposta não chegou — sem isso, a borda do 30º dia poderia
 * divergir entre o badge da linha e a faixa do topo, que é contada no servidor.
 */
export const DIAS_AVISO_VENCIMENTO_FALLBACK = 30;

export const DOCUMENTO_PADRAO_TIPO_LABEL: Record<DocumentoPadraoTipo, string> = {
  PROCURACAO: 'Procuração',
  CARTEIRA_OAB: 'Carteira da OAB',
  CONTRATO_SOCIAL: 'Contrato social',
  CONTRATO: 'Contrato',
  DOCUMENTO_PESSOAL: 'Documento pessoal',
  OUTRO: 'Outro',
};

export const DOCUMENTO_PADRAO_PRODUTO_LABEL: Record<DocumentoPadraoProduto, string> = {
  NOTIFICACAO: 'Notificação',
  NEGOCIACAO: 'Negociação',
  ARBITRAGEM: 'Arbitragem',
  MEDIACAO: 'Mediação',
};

export const DOCUMENTO_PADRAO_PRODUTOS: DocumentoPadraoProduto[] = [
  'NOTIFICACAO',
  'NEGOCIACAO',
  'ARBITRAGEM',
  'MEDIACAO',
];

export const DOCUMENTO_PADRAO_TIPOS: DocumentoPadraoTipo[] = [
  'PROCURACAO',
  'CARTEIRA_OAB',
  'CONTRATO_SOCIAL',
  'CONTRATO',
  'DOCUMENTO_PESSOAL',
  'OUTRO',
];

const MS_POR_DIA = 24 * 60 * 60 * 1000;

const inicioDoDia = (iso: string): number => {
  const data = new Date(iso);
  return Date.UTC(data.getUTCFullYear(), data.getUTCMonth(), data.getUTCDate());
};

export function diasAteVencimento(validoAte: string | null, referencia: string): number | null {
  if (!validoAte) return null;
  return Math.round((inicioDoDia(validoAte) - inicioDoDia(referencia)) / MS_POR_DIA);
}

export function situacaoValidade(
  validoAte: string | null,
  referencia: string,
  diasAviso: number = DIAS_AVISO_VENCIMENTO_FALLBACK,
): DocumentoPadraoSituacaoValidade {
  const dias = diasAteVencimento(validoAte, referencia);
  if (dias === null) return 'SEM_VALIDADE';
  if (dias < 0) return 'VENCIDA';
  if (dias <= diasAviso) return 'VENCENDO';
  return 'VIGENTE';
}

export function podeSerSugerido(documento: DocumentoPadrao, referencia: string): boolean {
  if (documento.arquivado) return false;
  return situacaoValidade(documento.validoAte, referencia) !== 'VENCIDA';
}

/**
 * Leitura do campo que o SERVIDOR contou — não mais um scan do array de usos, que só via a
 * primeira página. A trava de verdade é o 409 da exclusão; isto aqui é a antecipação visual dela.
 */
export function usosEmAndamento(documento: DocumentoPadrao): number {
  return documento.usosEmAndamento;
}

export function podeExcluir(documento: DocumentoPadrao): boolean {
  return documento.usosEmAndamento === 0;
}

export function nomeSugerido(
  tipo: DocumentoPadraoTipo,
  parte: DocumentoPadraoParte | null,
): string {
  const rotuloTipo = DOCUMENTO_PADRAO_TIPO_LABEL[tipo];
  if (!parte?.nome) return rotuloTipo;
  return `${rotuloTipo} — ${parte.nome}`;
}

export function rotuloEscopo(
  escopo: DocumentoPadraoEscopo,
  setoresIds: string[],
  setores: DocumentosPadraoSetor[],
): string {
  if (escopo === 'ESCRITORIO') return 'Todo o escritório';
  if (escopo === 'PESSOAL') return 'Só eu';

  const nomes = setoresIds
    .map((id) => setores.find((setor) => setor.id === id)?.nome)
    .filter((nome): nome is string => Boolean(nome));

  if (nomes.length === 0) return 'Setores específicos';
  if (nomes.length === 1) return `Setor ${nomes[0]}`;
  return `Setores ${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

export function rotuloProdutos(produtos: DocumentoPadraoProduto[]): string {
  if (produtos.length === 0) return 'Nenhum produto';
  if (produtos.length === DOCUMENTO_PADRAO_PRODUTOS.length) return 'Todos os produtos';
  return produtos.map((produto) => DOCUMENTO_PADRAO_PRODUTO_LABEL[produto]).join(' · ');
}

/**
 * Escopo, abas, busca, filtros, contadores e resumo de validade SAÍRAM daqui: viraram `where` no
 * servidor, sob uma regra única (`montarWhereDocumentosVisiveis`). O que sobrou neste arquivo é
 * apresentação — rótulos e a situação de validade, que o servidor parametriza mandando
 * `referencia` e `diasAvisoVencimento` na resposta.
 *
 * O motivo de não existirem mais as duas expressões: visibilidade escrita em dois lugares é
 * visibilidade que diverge, e o custo dessa divergência é documento de um escritório aparecendo
 * para outro.
 */

export const FILTROS_DOCUMENTOS_PADRAO_VAZIOS: DocumentosPadraoFiltros = {
  busca: '',
  tipo: 'todos',
  setorId: 'todos',
  produto: 'todos',
  validade: 'todas',
};

export function possuiFiltrosAtivos(filtros: DocumentosPadraoFiltros): boolean {
  return (
    filtros.busca.trim().length > 0 ||
    filtros.tipo !== 'todos' ||
    filtros.setorId !== 'todos' ||
    filtros.produto !== 'todos' ||
    filtros.validade !== 'todas'
  );
}
